import React, { useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

const ExperimentStyled = styled.div`
  position: relative;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
  transform-style: preserve-3d;
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  cursor: none;
  display: inline-block;
  width: fit-content;
  height: fit-content;
  
  &:hover {
    cursor: none !important;
  }
`;

const ExperimentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  max-width: ${props => props.maxWidth ? `${props.maxWidth}px` : 'none'};
  max-height: ${props => props.maxHeight ? `${props.maxHeight}px` : 'none'};
`;

const Shine = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(
    135deg,
    hsla(0, 0%, 0%, 0) 52%,

    hsla(0, 0%, 100%, 0.05) 55%,
    hsla(0, 0%, 0%, 0) 60%
  );

  pointer-events: none;
  opacity: 0; /* Initially hidden */
  transition: opacity 0.3s ease-in-out;
  background-size: 200% 200%; /* To allow smooth position transitions */
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  color: #ffffff;
`;

const Tags = styled.div`
  margin-top: 8px;
`;

const Tag = styled.span`
  background-color: #707070;
  border-radius: 4px;
  padding: 4px 8px;
  margin-right: 5px;
  font-size: 14px;
`;

const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  color: white;
  padding: 10px;
  box-sizing: border-box;
  z-index: 3;
`;

const Experiment = ({ experiment, onClick, onMouseEnter, onMouseLeave }) => {
  const cardRef = useRef(null);
  const shineRef = useRef(null);

  const colSpan = useMemo(() => Math.floor(Math.random() * 2) + 1, []);
  const rowSpan = useMemo(() => Math.floor(Math.random() * 2) + 1, []);

  const bgImage = experiment.media.images.length
    ? require(`../../images/Experiments/${experiment.media.images[0]}`)
    : require("../../images/placeholder.png");
  
  const handleMouseEnter = (e) => {
    if (onMouseEnter) onMouseEnter(e);
  };

  const handleMouseLeave = (e) => {
    if (onMouseLeave) onMouseLeave(e);
  };

  useEffect(() => {
    const card = cardRef.current;
    const shine = shineRef.current;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const rotateX = ((offsetY - centerY) / centerY) * 20;
      const rotateY = ((offsetX - centerX) / centerX) * 20;

      // Update card transform
      card.style.transform = `rotateX(${rotateX}deg) rotateY(${-rotateY}deg)`;
      card.style.boxShadow = `${-rotateY * 2}px ${
        rotateX * 2
      }px 15px rgba(0, 0, 0, 0.2)`;

      // Dynamically adjust shine position
      const shineX = (offsetX / rect.width) * 100; // Percentage across width
      const shineY = (offsetY / rect.height) * 100; // Percentage across height
      shine.style.backgroundPosition = `${shineX}% ${shineY}%`; // Set gradient position
    };

    const handleMouseEnter = () => {
      // Fade in shine
      shine.style.opacity = 1;
    };

    const handleMouseLeave = () => {
      // Reset card transform
      card.style.transform = "rotateX(0deg) rotateY(0deg)";
      card.style.boxShadow = "0 4px 10px rgba(0, 0, 0, 0.1)";

      // Fade out shine
      shine.style.opacity = 0;
    };

    card.addEventListener("mousemove", handleMouseMove);
    card.addEventListener("mouseenter", handleMouseEnter);
    card.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      card.removeEventListener("mousemove", handleMouseMove);
      card.removeEventListener("mouseenter", handleMouseEnter);
      card.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <ExperimentStyled
      ref={cardRef}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ExperimentImage 
        src={bgImage} 
        alt={experiment.title}
        maxWidth={experiment.size?.maxWidth}
        maxHeight={experiment.size?.maxHeight}
      />
      <Shine ref={shineRef} />
      <ContentOverlay>
        <Title>{experiment.title}</Title>
        <Tags>
          {experiment.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </Tags>
      </ContentOverlay>
    </ExperimentStyled>
  );
};

export default Experiment;
